/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { InputColumn } from '@g17eco/types/questionInterfaces';
import { UniversalTrackerValuePlain } from '@g17eco/types/universalTrackerValue';
import { ValueListPlain } from '@g17eco/types/valueList';
import { hasMinAndMaxColumnType, TableColumn, UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { getNumberScaleText, UnitTypes } from '@utils/units';
import { UnitConfig } from '@g17eco/types/survey';
import { formatCalculationNumber } from '@utils/formula';

// This file is different from @link universalTrackerValue.tsx by focusing on
// extract values once questions is answered and is a copy of functions from API

/** Extract correct data property based on setup **/
export const getUtrvDataProp = (utrv: Pick<UniversalTrackerValuePlain, 'valueData'>, userInput = false) => {
  if (!utrv.valueData) {
    return undefined
  }

  if (!userInput || !utrv.valueData.input) {
    return utrv.valueData.data;
  }

  const inputData = utrv.valueData.input.data;
  if (utrv.valueData.isImported) {
    // [GU-4066] Partial input with empty input.table, check
    const hasRootData = typeof utrv.valueData.data === 'object' && Object.keys(utrv.valueData.data).length > 0;
    const hasInputData = typeof inputData === 'object' && Object.keys(inputData).length > 0;
    if (hasRootData && !hasInputData) {
      return utrv.valueData.data;
    }
  }

  return inputData;
}


export const getUtrvTableProp = (utrv: Pick<UniversalTrackerValuePlain, 'valueData'>, userInput = false) => {
  if (!utrv.valueData) {
    return undefined
  }

  // No input means it was not submitted by user, should fallback to available
  if (!userInput || !utrv.valueData.input) {
    return utrv.valueData.table;
  }

  const table = utrv.valueData.input.table;
  if (utrv.valueData.isImported) {
    // [GU-4066] Partial input with empty input.table, check
    const hasRootData = Array.isArray(utrv.valueData.table) && utrv.valueData.table.length > 0;
    const hasInputData = Array.isArray(table) && table.length > 0;
    if (hasRootData && !hasInputData) {
      return utrv.valueData.table; // Broken import situation
    }
  }

  return table;
}

export const getUtrvValue = (utrv: Pick<UniversalTrackerValuePlain, 'value' | 'valueData'>, userInput = false) => {
  if (!userInput || !utrv.valueData || !utrv.valueData.input) {
    return utrv.value;
  }

  // [GU-4066] Partial input without input.value
  if (utrv.valueData.isImported && utrv.valueData.input.value === undefined) {
    return utrv.value;
  }

  return utrv.valueData.input.value;
}

export const getUnitCode = (
  utrv: Pick<UniversalTrackerValuePlain, 'unit' | 'valueData'>,
  utr: Pick<UniversalTrackerPlain, 'unit'>,
  userInput = false
) => {
  if (!userInput || !utrv.valueData || !utrv.valueData.input) {
    // weird that say mention unit is deprecated on frontend, but seems like it's used properly
    return utrv.unit || utr.unit;
  }

  if (utrv.valueData.isImported && utrv.valueData.input.unit === undefined) {
    // [GU-4066] Partial input with empty input.table, check
    return utrv.unit || utr.unit;
  }

  return utrv.valueData.input.unit;
}

export const getNumberScaleCode = (
  utrv: Pick<UniversalTrackerValuePlain, 'numberScale' | 'valueData'>,
  utr: Pick<UniversalTrackerPlain, 'numberScale'>,
  userInput = false
) => {
  if (!userInput || !utrv.valueData || !utrv.valueData.input) {
    return utrv.numberScale || utr.numberScale;
  }
  if (utrv.valueData.isImported && utrv.valueData.input.numberScale === undefined) {
    // [GU-4066] Partial input with empty input.table, check
    return utrv.numberScale || utr.numberScale;
  }
  return utrv.valueData.input.numberScale;
}

export const getTableNumberScaleCode = ({
  tableColumn,
  inputColumn,
  displayUserInput = false,
}: {
  tableColumn: TableColumn;
  inputColumn: InputColumn;
  displayUserInput?: boolean;
}): string => {
  if (displayUserInput) {
    // Because we might be dealing with no-input data, fallback to column
    return inputColumn.numberScale ?? tableColumn.numberScale ?? '';
  }

  return tableColumn.numberScale ?? '';
};

export const getTableUnitCode = (
  tableColumn: TableColumn,
  rowData: InputColumn,
  displayUserInput = false,
): string => {
  if (displayUserInput) {
    // Because we might be dealing with no-input data, fallback to column
    return rowData.unit ?? tableColumn.unit ?? ''
  }
  return tableColumn.unit ?? '';
}

const getDisplayUnit = (params: {
  tableColumn: TableColumn;
  unitConfig: UnitConfig | undefined;
  inputColumn?: InputColumn;
  displayUserInput?: boolean;
}): string => {
  const { tableColumn: c, unitConfig, inputColumn, displayUserInput = true } = params;
  // Percentage columns always use '%'
  if (hasMinAndMaxColumnType(c.type, c.validation?.min, c.validation?.max)) {
    return '%';
  }
  // Currency columns: unitConfig.currency always wins (survey config priority)
  if (c.unitType && c.unitType === UnitTypes.currency) {
    return unitConfig?.currency || '';
  }
  // Non-currency columns: use row unit if displayUserInput is true
  if (displayUserInput && inputColumn?.unit) {
    return inputColumn.unit;
  }
  return c.unit ?? '';
};

export const getValueFromOptions = (params: {
  column: TableColumn;
  valueListMap: Map<string, Pick<ValueListPlain, 'options'>>;
  value: number | string | undefined;
  unit: string;
}) => {
  const { column, valueListMap, value, unit } = params;
  if (value === undefined) {
    return;
  }

  if (column.options && column.options.length > 0) {
    const op = column.options.find((o) => o.code === value);
    return op ? op.name : value;
  }

  if (column.listId) {
    const options = valueListMap.get(column.listId)?.options ?? [];
    const op = options.find((o) => o.code === value);
    return op ? op.name : value;
  }

  return `${value}${unit}`;
};

const getValueByColumn = (d: InputColumn[] | Record<string, string | number | undefined>, c: TableColumn) => {
  if (Array.isArray(d)) {
    const column = d.find((col) => col.code === c.code);
    return column ? column.value : '';
  }
  return d[c.code];
};

const getUnitNumberScale = (params: {
  data: InputColumn[] | Record<string, string | number | undefined>;
  column: TableColumn;
  unitConfig: UnitConfig | undefined;
  displayUserInput?: boolean;
}) => {
  const { data, column, unitConfig, displayUserInput = true } = params;

  const inputColumn = Array.isArray(data) ? data.find((col) => col.code === column.code) : undefined;
  const unit = getDisplayUnit({ tableColumn: column, unitConfig, inputColumn, displayUserInput });

  if (!Array.isArray(data)) {
    return { unit, numberScale: undefined };
  }

  const numberScaleCode = inputColumn
    ? getTableNumberScaleCode({ tableColumn: column, inputColumn, displayUserInput })
    : undefined;

  return {
    numberScale: numberScaleCode ? getNumberScaleText(numberScaleCode, '') : undefined,
    unit,
  };
};

export const getFormattedValueByColumn = (params: {
  data: InputColumn[] | Record<string, string | number | undefined>;
  column: TableColumn;
  unitConfig: UnitConfig | undefined;
  valueListMap: Map<string, Pick<ValueListPlain, 'options'>>;
}) => {
  const { data, column, unitConfig, valueListMap } = params;
  const value = getValueByColumn(data, column);
  if (value === undefined || value === '') {
    return '';
  }

  const decimal = column.validation?.decimal;

  if (column?.calculation?.formula) {
    return formatCalculationNumber(value, decimal);
  }

  const { unit, numberScale } = getUnitNumberScale({ data, column, unitConfig });

  if (Array.isArray(value)) {
    return value.map((v) => getValueFromOptions({ column, valueListMap, value: v, unit })).join(', ');
  }
  const numberScaleDisplay = numberScale ? ` ${numberScale}` : '';
  return `${getValueFromOptions({ column, valueListMap, value, unit }) ?? ''}${numberScaleDisplay}`;
};
